import { zodMoment } from '@qapline/helpers';
import { T } from '@qapline/translations';
import { carrierPlaceSchema, dispatcherVehicleFeature } from 'modules/carriers';
import { z } from 'zod';

export const dispatcherVehicleSchema = () =>
    z.object({
        dispatcherVehicle_id: z.number().nullish(),
        vehicleType_id: z.number(),
        maxHeight: z.number(),
        maxWidth: z.number(),
        maxLength: z.number(),
        maxWeight: z.number(),
        // RHF registers one field per rendered equipment checkbox, leaving undefined
        // holes when none are checked. Drop them so a vehicle with no equipment is valid.
        vehicleVehicleFeatures: z.preprocess(
            (value) => (Array.isArray(value) ? value.filter(Boolean) : value),
            z.array(dispatcherVehicleFeature()),
        ),
        // other
        dispatcher_id: z.number().nullish(),
    });

export const onboardingBasicInfoSchema = (t: T) =>
    z.object({
        // personal info
        firstName: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        lastName: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        phone: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .regex(new RegExp(/(?:\(?\+\d{2}\)?\s*)?\d+(?:[ -]*\d+)*$/), t('form.errors.phoneFormat'))
            .nullable()
            .or(z.literal('')),

        // login credential, globally unique
        email: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.emailFormat'),
            })
            .min(1, { message: t('form.errors.required') })
            .email({
                message: t('auth.email.error.validEmail'),
            }),
        // where communication goes; several dispatchers may share one mailbox
        contactEmail: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.emailFormat'),
            })
            .min(1, { message: t('form.errors.required') })
            .email({
                message: t('auth.email.error.validEmail'),
            }),
        // auth info
        password: z.string({
            required_error: t('form.errors.required'),
            invalid_type_error: t('form.errors.emailFormat'),
        }),
        confirmPassword: z.string({
            required_error: t('form.errors.required'),
            invalid_type_error: t('form.errors.emailFormat'),
        }),
        // offers notifications
        notificationEmail: z.boolean(),
        notificationWhatsapp: z.boolean(),
        // other
        carrier_id: z.number(),
        dispatcher_id: z.number().nullish(),
        language_id: z
            .number({
                required_error: t('form.errors.required'),
            })
            .min(1, { message: t('form.errors.required') }),
    });

export const onboardingVehiclesInfoSchema = () =>
    z.object({
        // details of the vehicle fleet
        deliveryVehicle: z.boolean(),
        soloVehicle: z.boolean(),
        semiTrailerVehicle: z.boolean(),
        combinationVehicle: z.boolean(),
        // detail of vehicles
        dispatcherVehicles: z.array(dispatcherVehicleSchema()),
    });

export const onboardingLocationsInfoSchema = (t: T) => z.object({ places: z.array(carrierPlaceSchema(t)) });

export const onboardingSchema = (t: T) =>
    onboardingBasicInfoSchema(t).merge(onboardingVehiclesInfoSchema()).merge(onboardingLocationsInfoSchema(t));

export const getOneDispatcherResponseSchema = (t: T) =>
    z.object({
        dispatcher_id: z.number(),
        notificationEmail: z.boolean(),
        notificationWhatsapp: z.boolean(),
        deleted: z.boolean(),
        contactEmail: z.string(),
        carrier_id: z.number(),
        user_id: z.number(),
        user: z.object({
            user_id: z.number(),
            number: z.number(),
            name: z.string(),
            surname: z.string(),
            email: z.string(),
            profilePicture: z.string().nullable(),
            tsAdded: zodMoment(),
            tsLastLogin: zodMoment().nullable(),
            deleted: z.boolean().optional(),
            contactInfo: z
                .object({
                    email: z.string().nullable().optional(),
                    phone: z.string().nullable().optional(),
                    note: z.string().nullable().optional(),
                    language: z
                        .object({
                            language_id: z.number(),
                            languageCode: z.string(),
                        })
                        .nullable()
                        .optional(),
                })
                .nullable()
                .optional(),
        }),
        carrier: z.any().optional(),
        vehicles: z
            .array(
                z.object({
                    vehicle_id: z.number(),
                    dispatcher_id: z.number(),
                    vehicleType_id: z.number(),
                    maxHeight: z.coerce.number().nullable(),
                    maxLength: z.coerce.number().nullable(),
                    maxWeight: z.coerce.number().nullable(),
                    maxWidth: z.coerce.number().nullable(),
                    deleted: z.boolean().optional(),
                    vehicleVehicleFeatures: z
                        .array(
                            z.object({
                                vehicleVehicleFeature_id: z.number(),
                                dispatcherVehicle_id: z.number(),
                                vehicleFeature_id: z.number(),
                                vehicleFeature: z.any().optional(),
                            }),
                        )
                        .optional(),
                    vehicleType: z.any().optional(),
                }),
            )
            .optional(),
        places: z.array(carrierPlaceSchema(t)).optional(),
    });

export type DecodedQueryParams = {
    role: 'onboarding';
    carrier_id: number;
    dispatcher_id?: number;
    iat: number;
    exp: number;
};

export type DispatcherVehicleSchema = z.infer<ReturnType<typeof dispatcherVehicleSchema>>;
export type OnboardingBasicInfoSchema = z.infer<ReturnType<typeof onboardingBasicInfoSchema>>;
export type OnboardingVehiclesInfoSchema = z.infer<ReturnType<typeof onboardingVehiclesInfoSchema>>;
export type OnboardingLocationsInfoSchema = z.infer<ReturnType<typeof onboardingLocationsInfoSchema>>;
export type OnboardingSchema = z.infer<ReturnType<typeof onboardingSchema>>;
export type GetOneDispatcherResponseSchema = z.infer<ReturnType<typeof getOneDispatcherResponseSchema>>;
